import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { Button, Card, EmptyState, Field, LoadingRegion } from '@gatecheckpro/design-system';
import { timezoneSchema, type Venue } from '@gatecheckpro/contracts';
import { useCommand } from '../lib/useCommand.ts';
import { useResource } from '../lib/useResource.ts';
import { FailureNotice } from '../lib/refusal.tsx';

interface VenueSummary {
  readonly venueId: string;
  readonly name: string;
  readonly publicLocality: string;
  readonly countryCode: string;
  readonly timezone: string;
}

/**
 * Venues.
 *
 * Managed at organization level rather than per event, because a venue is a
 * physical place an organizer uses repeatedly. Its zones and gates are set up
 * once; each event then decides how those gates behave on the night.
 */
export function VenuesPage() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const base = `/api/v1/organizer/organizations/${organizationId ?? ''}`;
  const { resource, reload } = useResource<{ venues: VenueSummary[] }>(`${base}/venues`);
  const command = useCommand<Venue>();

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [locality, setLocality] = useState('');
  const [addressLine, setAddressLine] = useState('');
  const [timezone, setTimezone] = useState('Africa/Lagos');
  const [countryCode, setCountryCode] = useState('NG');

  const timezoneProblem = timezoneSchema.safeParse(timezone).success
    ? undefined
    : 'Use a region name such as Africa/Lagos.';

  async function create(formEvent: React.FormEvent): Promise<void> {
    formEvent.preventDefault();
    const result = await command.run(`${base}/venues`, {
      method: 'POST',
      body: {
        name,
        publicLocality: locality,
        publicAddressLine: addressLine === '' ? null : addressLine,
        countryCode,
        timezone,
        internalNotes: null,
      },
    });
    if (result !== null) {
      setName('');
      setLocality('');
      setAddressLine('');
      setShowForm(false);
      reload();
    }
  }

  if (resource.status === 'loading') return <LoadingRegion label="Loading venues" />;
  if (resource.status === 'failed') return <FailureNotice error={resource.error} />;

  return (
    <div className="gc-stack gc-stack--loose">
      <div className="gc-row gc-row--between">
        <div className="gc-stack gc-stack--tight">
          <h2>Venues</h2>
          <p className="gc-muted">
            The places you put events on. Zones and gates are configured here once and reused.
          </p>
        </div>
        <Button variant="primary" onClick={() => setShowForm((current) => !current)}>
          {showForm ? 'Cancel' : 'Add a venue'}
        </Button>
      </div>

      <FailureNotice error={command.error} />

      {showForm && (
        <Card title="New venue">
          <form className="gc-stack" onSubmit={(formEvent) => void create(formEvent)}>
            <Field
              label="Name"
              required
              placeholder="Eko Convention Centre"
              value={name}
              onChange={(input) => setName(input.target.value)}
            />
            <Field
              label="Public locality"
              required
              placeholder="Victoria Island, Lagos"
              hint="What attendees see. Enough to find it, not an operational address."
              value={locality}
              onChange={(input) => setLocality(input.target.value)}
            />
            <Field
              label="Public address line"
              placeholder="1 Adetokunbo Ademola Street"
              value={addressLine}
              onChange={(input) => setAddressLine(input.target.value)}
            />
            <div className="gc-grid">
              <Field
                label="Country"
                required
                maxLength={2}
                value={countryCode}
                onChange={(input) => setCountryCode(input.target.value.toUpperCase())}
              />
              <Field
                label="Timezone"
                required
                error={timezoneProblem}
                hint="The venue's own clock."
                value={timezone}
                onChange={(input) => setTimezone(input.target.value)}
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              busy={command.busy}
              disabled={name.length < 2 || locality.length < 2 || timezoneProblem !== undefined}
            >
              Add venue
            </Button>
          </form>
        </Card>
      )}

      {resource.data.venues.length === 0 ? (
        <EmptyState title="No venues yet">
          Add the place your first event happens. You will define its entrances and zones next, and
          every event there reuses them.
        </EmptyState>
      ) : (
        <div className="gc-grid">
          {resource.data.venues.map((venue) => (
            <Link key={venue.venueId} to={venue.venueId} className="gc-event-card">
              <span className="gc-event-card__title">{venue.name}</span>
              <span className="gc-muted">{venue.publicLocality}</span>
              <span className="gc-muted">
                {venue.countryCode} · {venue.timezone}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
